import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * Lightweight toast notification system.
 *
 * App-wide usage (preferred): <ToastProvider> wraps the app and mounts a single
 * <ToastContainer/>; any component calls
 *   const { addToast } = useToast();
 *   addToast('Page created!', 'success');   // type: 'success' | 'error' | 'info' | 'warning'
 *
 * Without a provider, useToast() falls back to local state and the caller
 * renders its own <ToastContainer toasts={toasts} onRemove={removeToast} />.
 */

const ToastContext = createContext(null);

const useToastState = () => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
};

export const ToastProvider = ({ children }) => {
  const value = useToastState();
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={value.toasts} onRemove={value.removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Hooks must run unconditionally; the local state is simply unused when a provider exists.
  const local = useToastState();
  return ctx || local;
};

/* Solid surface with a coloured accent bar; the accent colours are the shared tokens. */
const TOAST_STYLES = {
  success: { accent: 'var(--color-success)', icon: '✓' },
  error:   { accent: 'var(--color-danger)', icon: '✕' },
  info:    { accent: 'var(--color-primary)', icon: 'ℹ' },
  warning: { accent: 'var(--color-warning)', icon: '⚠' },
};

const Toast = ({ toast, onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration);
    return () => clearTimeout(timer);
  }, [toast, onRemove]);

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderRadius: 'var(--radius)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderLeft: `4px solid ${style.accent}`,
        color: 'var(--color-text)',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: 'var(--shadow-md)',
        minWidth: '280px',
        maxWidth: '420px',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateX(100%)' : 'translateX(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        cursor: 'pointer',
      }}
      onClick={() => {
        setExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
      }}
    >
      <span style={{ fontSize: '16px', fontWeight: '700', flexShrink: 0, color: style.accent }}>{style.icon}</span>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <span style={{ opacity: 0.5, fontSize: '12px', flexShrink: 0 }}>✕</span>
    </div>
  );
};

export const ToastContainer = ({ toasts, onRemove }) => {
  if (!toasts.length) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 10001,
      display: 'flex',
      flexDirection: 'column-reverse',
      gap: '8px',
    }}>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};
